import React, {useState, useEffect, useRef, useMemo, useCallback} from "react";
import "./Dashboard.css";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import Header from "./Header";
import Col from "react-bootstrap/Col";
import EventStream from "./EventStream";
import Search from "./Search/index.jsx";
import UserInfo from "./UserInfo/index.jsx";
import BondsList from "./BondsList/index.jsx";
import HomesList from "./HomesList/index.jsx";
import HomeCard from "./Cards/HomeCard/index.jsx";
import MatesList from "./MatesList/index.jsx";
import SmartByBondList from "./SmartByBondList/index.jsx";
import DeviceCard from "./Cards/DeviceCard";
import SceneCard from "./Cards/SceneCard";
import SkedCard from "./Cards/SkedsCard";
import GroupCard from "./Cards/GroupCard";
import SidekickCard from "./Cards/SidekickCard";
import BreezeProCard from "./Cards/BreezeProCard";
import { useAuth } from "../../../../contexts/AuthContext";
import { getUserById, getBondById, getBondStatus, getUserHomes, getHome } from "../../libs/dashboard";


function Dashboard() {
    const { getToken } = useAuth();
    const [isSearching, setIsSearching] = useState(true);

    const [selectedUser, setSelectedUser] = useState(null);
    const [lastLoadedUser, setLastLoadedUser] = useState(null); // Track the last successfully loaded user

    // Layout state: toggle between 1/3 and 2/3
    const [layoutIndex, setLayoutIndex] = useState(0); // 0 = 1/3, 1 = 2/3
    const layouts = [
        { cards: 4, events: 8, label: '1/3' },
        { cards: 8, events: 4, label: '2/3' }
    ];
    const currentLayout = layouts[layoutIndex];

    const [userData, setUserData] = useState(null);
    const [userDataLoading, setUserDataLoading] = useState(false);
    const [userDataError, setUserDataError] = useState(null);

    const [bondsData, setBondsData] = useState({});
    const [bondsLoading, setBondsLoading] = useState({});
    const [bondsError, setBondsError] = useState({});

    const [homesList, setHomesList] = useState([]);
    const [homesLoading, setHomesLoading] = useState(false);
    const [homesError, setHomesError] = useState(null);
    const [selectedHomeId, setSelectedHomeId] = useState(null);

    const [homeDetail, setHomeDetail] = useState(null);
    const [homeDetailLoading, setHomeDetailLoading] = useState(false);
    const [homeDetailError, setHomeDetailError] = useState(null);

    // The bonds every dashboard panel renders. When a home is selected these are that
    // home's bonds; otherwise the user's own (legacy owner) bonds. Bridges, Smart by
    // Bond, Devices, Groups, Scenes, Skeds, Sidekicks, Mates and the livelog stream are
    // all driven by this one set, so they reflect ("filter to") the selected home.
    const activeBonds = useMemo(() => {
        if (selectedHomeId) return homeDetail?.bonds || [];
        return userData?.["bonds"] || [];
    }, [selectedHomeId, homeDetail, userData]);

    const fetchedBondsRef = useRef(new Set());

    // Update page title based on user selection
    useEffect(() => {
        if (userData && userData.email) {
            document.title = `Bond Dashboard - ${userData.email}`;
        } else {
            document.title = 'Bond Dashboard';
        }
    }, [userData]);

    // Load user from URL hash on mount
    useEffect(() => {
        const loadUserFromHash = () => {
            const hash = window.location.hash;
            // Match #/u/<user-id> and #/u/<user-id>/home/<home-id>
            const match = hash.match(/^#\/u\/([^/]+)(?:\/home\/([^/]+))?$/);
            if (match && match[1]) {
                const userId = decodeURIComponent(match[1]);
                setSelectedUser(userId);
                setSelectedHomeId(match[2] ? decodeURIComponent(match[2]) : null);
                setIsSearching(false);
            } else if (hash === '' || hash === '#' || hash === '#/') {
                // No hash or root hash - go to search mode
                // Don't clear selectedUser - keep the data loaded
                setIsSearching(true);
            }
        };

        loadUserFromHash();

        // Listen for hash changes (browser back/forward)
        const handleHashChange = () => {
            loadUserFromHash();
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    useEffect(() => {
        if (!selectedUser) {
            // Don't clear data if no user is selected - keep previous data
            return;
        }

        // Only fetch/clear if this is a different user than the last loaded one
        if (selectedUser === lastLoadedUser) {
            return;
        }

        // Clear data only when loading a NEW different user
        setUserData(null);
        setUserDataError(null);
        setBondsData({});
        setBondsLoading({});
        setBondsError({});
        fetchedBondsRef.current.clear();
        setHomesList([]);

        const fetchUserData = async () => {
            setUserDataLoading(true);
            setUserDataError(null);

            try {
                const token = getToken();
                const data = await getUserById(token, selectedUser);
                setUserData(data);
                setLastLoadedUser(selectedUser); // Mark this user as successfully loaded
            } catch (err) {
                console.error('Error fetching user data:', err);
                setUserDataError(err.message);
            } finally {
                setUserDataLoading(false);
            }
        };

        fetchUserData();
    }, [selectedUser, lastLoadedUser, getToken]);

    // Bumped when a HOME event arrives, to re-run both homes fetches below.
    const [homesReloadKey, setHomesReloadKey] = useState(0);
    const reloadHomes = useCallback(() => setHomesReloadKey(k => k + 1), []);
    // Which home's detail is currently loaded, so a reload can tell itself
    // apart from a switch to a different home.
    const loadedHomeIdRef = useRef(null);

    // Fetch the list of homes the selected user belongs to
    useEffect(() => {
        if (!selectedUser) return;
        let cancelled = false;
        const fetchHomes = async () => {
            setHomesLoading(true);
            setHomesError(null);
            try {
                const token = getToken();
                const data = await getUserHomes(token, selectedUser);
                if (!cancelled) setHomesList(data?.results || []);
            } catch (err) {
                console.error('Error fetching homes:', err);
                if (!cancelled) setHomesError(err.message);
            } finally {
                if (!cancelled) setHomesLoading(false);
            }
        };
        fetchHomes();
        return () => { cancelled = true; };
    }, [selectedUser, getToken, homesReloadKey]);

    // Default-select the most recently created home once the list loads.
    // An explicit selection from the URL or a click (non-null selectedHomeId) always wins.
    useEffect(() => {
        if (selectedHomeId) return;
        if (!homesList || homesList.length === 0) return;
        const latest = [...homesList].sort(
            (a, b) => new Date(b.created || 0) - new Date(a.created || 0)
        )[0];
        if (latest) setSelectedHomeId(latest.id);
    }, [homesList, selectedHomeId]);

    // Fetch the selected home's detail (metadata + members + invites + bonds)
    useEffect(() => {
        if (!selectedHomeId) {
            setHomeDetail(null);
            loadedHomeIdRef.current = null;
            return;
        }
        // Clear the previous home's data so its card never shows while the new
        // one loads — but not on a reload of the same home, which refreshes in
        // place instead of blanking the card.
        if (loadedHomeIdRef.current !== selectedHomeId) {
            setHomeDetail(null);
            loadedHomeIdRef.current = selectedHomeId;
        }
        let cancelled = false;
        const fetchHomeDetail = async () => {
            setHomeDetailLoading(true);
            setHomeDetailError(null);
            try {
                const token = getToken();
                const data = await getHome(token, selectedHomeId);
                if (!cancelled) setHomeDetail(data);
            } catch (err) {
                console.error('Error fetching home detail:', err);
                if (!cancelled) setHomeDetailError(err.message);
            } finally {
                if (!cancelled) setHomeDetailLoading(false);
            }
        };
        fetchHomeDetail();
        return () => { cancelled = true; };
    }, [selectedHomeId, getToken, homesReloadKey]);

    const handleRefresh = async () => {
        if (!selectedUser || userDataLoading) return;
        // Clear bonds cache so they re-fetch, but keep existing data visible while loading
        fetchedBondsRef.current.clear();
        setUserDataLoading(true);
        setUserDataError(null);
        try {
            const token = getToken();
            const data = await getUserById(token, selectedUser);
            setUserData(data); // new object reference triggers bonds useEffect → re-fetches all bonds
            setLastLoadedUser(selectedUser);
        } catch (err) {
            console.error('Error refreshing user data:', err);
            setUserDataError(err.message);
        } finally {
            setUserDataLoading(false);
        }
    };

    useEffect(() => {
        if (activeBonds.length === 0) return;

        const fetchBondsData = async () => {
            for (const bond of activeBonds) {
                const bondId = bond.bond_id;

                // Skip if already fetched or currently fetching
                if (fetchedBondsRef.current.has(bondId)) continue;

                // Mark as being fetched
                fetchedBondsRef.current.add(bondId);
                setBondsLoading(prev => ({...prev, [bondId]: true}));

                try {
                    const token = getToken();
                    const data = await getBondById(token, bondId);
                    setBondsData(prev => ({...prev, [bondId]: data}));
                    setBondsError(prev => ({...prev, [bondId]: null}));
                } catch (err) {
                    console.error(`Error fetching bond data for ${bondId}:`, err);
                    setBondsError(prev => ({...prev, [bondId]: err.message}));
                } finally {
                    setBondsLoading(prev => ({...prev, [bondId]: false}));
                }
            }
        };

        fetchBondsData();
    }, [activeBonds, getToken]);

    // Poll bond connectivity status with inactivity detection
    useEffect(() => {
        if (activeBonds.length === 0) return;

        const INACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes in milliseconds
        const POLL_INTERVAL = 10000;

        let lastActivityTime = Date.now();
        let intervalId = null;
        const bondsToSkip = new Set(); // Track bonds with empty responses

        const pollBondStatus = async (bondId) => {
            try {
                const token = getToken();
                const statusData = await getBondStatus(token, bondId);

                // Check if response is empty
                if (!statusData || Object.keys(statusData).length === 0) {
                    // Empty response - stop polling for this bond
                    bondsToSkip.add(bondId);
                    return;
                }

                // Update the connectivity data in bondsData
                setBondsData(prev => {
                    if (!prev[bondId]) return prev;

                    const currentConnectivity = prev[bondId].connectivity || {};
                    const newConnected = statusData.connected;
                    const newTimestamp = statusData.timestamp;

                    // Only update if something changed
                    if (currentConnectivity.connected === newConnected &&
                        currentConnectivity.timestamp === newTimestamp) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [bondId]: {
                            ...prev[bondId],
                            connectivity: {
                                connected: newConnected,
                                timestamp: newTimestamp
                            }
                        }
                    };
                });
            } catch (err) {
                console.error(`Error polling status for ${bondId}:`, err);
            }
        };

        // Poll all bonds only if user is active
        const pollAllBonds = () => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityTime;

            if (timeSinceLastActivity < INACTIVITY_TIMEOUT) {
                activeBonds.forEach(bond => {
                    // Skip bonds that returned empty responses
                    if (!bondsToSkip.has(bond.bond_id)) {
                        pollBondStatus(bond.bond_id);
                    }
                });
            }
        };

        // Track user activity
        const updateActivity = () => {
            const wasInactive = Date.now() - lastActivityTime >= INACTIVITY_TIMEOUT;
            lastActivityTime = Date.now();

            // If was inactive and now active, resume polling immediately
            if (wasInactive) {
                pollAllBonds();
            }
        };

        // Add activity listeners
        const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(event => {
            window.addEventListener(event, updateActivity, { passive: true });
        });

        // Initial poll
        pollAllBonds();

        // Set up polling interval (every 5 seconds)
        intervalId = setInterval(pollAllBonds, POLL_INTERVAL);

        // Cleanup on unmount or when userData changes
        return () => {
            if (intervalId) clearInterval(intervalId);
            activityEvents.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
        };
    }, [activeBonds, getToken]);

    function onSelectedUser(user_id, bond_id) {
        setIsSearching(false)
        setSelectedUser(user_id)
        // Update URL hash using new pattern
        window.location.hash = `#/u/${encodeURIComponent(user_id)}`;
    }

    function onSelectHome(homeId) {
        setSelectedHomeId(homeId);
        window.location.hash = `#/u/${encodeURIComponent(selectedUser)}/home/${encodeURIComponent(homeId)}`;
    }

    function toggleLayout() {
        setLayoutIndex((prevIndex) => prevIndex === 0 ? 1 : 0);
    }

    // Separate bonds, mates, and Smart by Bond based on bond_id prefix
    const getRegularBonds = () => {
        if (!activeBonds) return [];
        return activeBonds.filter(bond => {
            const firstChar = bond.bond_id.charAt(0);
            // Exclude Mates (SM), Smart by Bond (T, K, H)
            return !bond.bond_id.startsWith('SM') &&
                   firstChar !== 'T' && firstChar !== 'K' && firstChar !== 'H';
        });
    };

    const getMates = () => {
        if (!activeBonds) return [];
        return activeBonds.filter(bond => bond.bond_id.startsWith('SM'));
    };

    const getSmartByBonds = () => {
        if (!activeBonds) return [];
        return activeBonds.filter(bond => {
            const firstChar = bond.bond_id.charAt(0);
            return firstChar === 'T' || firstChar === 'K' || firstChar === 'H';
        });
    };

    // Extract all unique bond timezones from all bonds
    const getAllBondTimezones = () => {
        const timezones = new Set();
        if (activeBonds && activeBonds.length > 0) {
            for (const bond of activeBonds) {
                const bondData = bondsData[bond.bond_id];
                if (bondData) {
                    const timezone = bondData.sys?.time?.tz || null;
                    if (timezone) {
                        timezones.add(timezone);
                    }
                }
            }
        }
        return Array.from(timezones);
    };

    // Extract bond timezone from the first available bond (if any)
    const getBondTimezone = () => {
        // Get timezone from the first bond that has timezone data
        if (activeBonds && activeBonds.length > 0) {
            for (const bond of activeBonds) {
                const bondData = bondsData[bond.bond_id];
                if (bondData) {
                    const timezone = bondData.sys?.time?.tz || null;

                    if (timezone) {
                        return timezone;
                    }
                }
            }
        }
        return null;
    };

    // Aggregate devices from the given bonds (looked up in bondsData)
    const getDevicesForBonds = (bonds) => {
        const devices = [];
        (bonds || []).forEach(bond => {
            const bondData = bondsData[bond.bond_id];
            if (bondData?.devices) {
                // Handle both array and object formats
                const devicesList = Array.isArray(bondData.devices)
                    ? bondData.devices
                    : Object.values(bondData.devices);

                devicesList.forEach(device => {
                    devices.push({
                        ...device,
                        bond_id: bond.bond_id
                    });
                });
            }
        });
        return devices;
    };

    // Aggregate all devices from the user's legacy (owner_id) bonds
    const getAllDevices = () => getDevicesForBonds(activeBonds || []);

    // Aggregate all scenes from all bonds, merging scenes with same scene_id
    const getAllScenes = () => {
        const scenesMap = {};
        if (activeBonds) {
            activeBonds.forEach(bond => {
                const bondData = bondsData[bond.bond_id];
                if (bondData?.scenes) {
                    // Handle both array and object formats
                    const scenesList = Array.isArray(bondData.scenes)
                        ? bondData.scenes
                        : Object.values(bondData.scenes);

                    scenesList.forEach(scene => {
                        const sceneId = scene.scene_id || scene.id;

                        if (scenesMap[sceneId]) {
                            // Merge scene data
                            const existing = scenesMap[sceneId];

                            // Merge bond_ids
                            if (!existing.bond_ids.includes(bond.bond_id)) {
                                existing.bond_ids.push(bond.bond_id);
                            }

                            // Merge actors
                            if (scene.actors) {
                                existing.actors = [...(existing.actors || []), ...(Array.isArray(scene.actors) ? scene.actors : [scene.actors])];
                            }

                            // Merge types
                            if (scene.types) {
                                const existingTypes = existing.types || [];
                                const newTypes = Array.isArray(scene.types) ? scene.types : [scene.types];
                                existing.types = [...new Set([...existingTypes, ...newTypes])];
                            }

                            // Merge locations
                            if (scene.locations) {
                                const existingLocations = existing.locations || [];
                                const newLocations = Array.isArray(scene.locations) ? scene.locations : [scene.locations];
                                existing.locations = [...new Set([...existingLocations, ...newLocations])];
                            }

                            // Merge schedules
                            if (scene.schedules) {
                                existing.schedules = {...(existing.schedules || {}), ...scene.schedules};
                            }

                            // Store raw scene objects for JSON viewer
                            existing.raw_scenes.push(scene);
                        } else {
                            // First time seeing this scene
                            scenesMap[sceneId] = {
                                ...scene,
                                bond_ids: [bond.bond_id],
                                actors: scene.actors || [],
                                types: scene.types || [],
                                locations: scene.locations || [],
                                raw_scenes: [scene] // Store original scene objects
                            };
                        }
                    });
                }
            });
        }
        return Object.values(scenesMap);
    };

    // Aggregate all groups from all bonds, merging groups with same group_id
    const getAllGroups = () => {
        const groupsMap = {};
        if (activeBonds) {
            activeBonds.forEach(bond => {
                const bondData = bondsData[bond.bond_id];
                if (bondData?.groups) {
                    // Handle both array and object formats
                    const groupsList = Array.isArray(bondData.groups)
                        ? bondData.groups
                        : Object.values(bondData.groups);

                    groupsList.forEach(group => {
                        const groupId = group.group_id || group.id;

                        if (groupsMap[groupId]) {
                            // Merge group data
                            const existing = groupsMap[groupId];

                            // Merge bond_ids
                            if (!existing.bond_ids.includes(bond.bond_id)) {
                                existing.bond_ids.push(bond.bond_id);
                            }

                            // Merge devices/device_ids into a single devices array
                            const existingDevices = existing.devices || [];
                            const newDevices = [];
                            if (group.devices) {
                                newDevices.push(...(Array.isArray(group.devices) ? group.devices : [group.devices]));
                            }
                            if (group.device_ids) {
                                newDevices.push(...(Array.isArray(group.device_ids) ? group.device_ids : [group.device_ids]));
                            }
                            if (newDevices.length > 0) {
                                existing.devices = [...new Set([...existingDevices, ...newDevices])];
                            }

                            // Merge device_types
                            if (group.device_types) {
                                const existingTypes = existing.device_types || [];
                                const newTypes = Array.isArray(group.device_types) ? group.device_types : [group.device_types];
                                existing.device_types = [...new Set([...existingTypes, ...newTypes])];
                            }

                            // Merge actions
                            if (group.actions) {
                                const existingActions = existing.actions || [];
                                const newActions = Array.isArray(group.actions) ? group.actions : [group.actions];
                                existing.actions = [...new Set([...existingActions, ...newActions])];
                            }

                            // Merge schedules
                            if (group.schedules) {
                                existing.schedules = {...(existing.schedules || {}), ...group.schedules};
                            }

                            // Store raw group objects for JSON viewer
                            existing.raw_groups.push(group);
                        } else {
                            // First time seeing this group
                            // Consolidate devices and device_ids into a single devices array
                            const allDevices = [];
                            if (group.devices) {
                                allDevices.push(...(Array.isArray(group.devices) ? group.devices : [group.devices]));
                            }
                            if (group.device_ids) {
                                allDevices.push(...(Array.isArray(group.device_ids) ? group.device_ids : [group.device_ids]));
                            }

                            groupsMap[groupId] = {
                                ...group,
                                bond_ids: [bond.bond_id],
                                devices: [...new Set(allDevices)], // Deduplicate
                                device_types: group.device_types || [],
                                actions: group.actions || [],
                                raw_groups: [group] // Store original group objects
                            };
                        }
                    });
                }
            });
        }
        return Object.values(groupsMap);
    };

    // Calculate schedule time based on mark and seconds
    const calculateScheduleTime = (mark, seconds, bondTimezone) => {
        if (seconds === undefined) return null;

        // For midnight, calculate the actual time
        if (mark === 'midnight') {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;

            // Format as HH:MM:SS or HH:MM if no seconds
            if (secs === 0) {
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            }
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        }

        // For astronomical events, show as relative time
        const eventNames = {
            'sunrise': 'Sunrise',
            'sunset': 'Sunset',
            'dusk': 'Dusk',
            'dawn': 'Dawn'
        };

        if (eventNames[mark]) {
            if (seconds === 0) {
                return eventNames[mark];
            }
            const absSeconds = Math.abs(seconds);
            const hours = Math.floor(absSeconds / 3600);
            const minutes = Math.floor((absSeconds % 3600) / 60);

            let offset = '';
            if (hours > 0) {
                offset = `${hours}h ${minutes}m`;
            } else {
                offset = `${minutes}m`;
            }

            if (seconds >= 0) {
                return `${eventNames[mark]} + ${offset}`;
            } else {
                return `${eventNames[mark]} - ${offset}`;
            }
        }

        return null;
    };

    // Aggregate all schedules from all devices, groups, and scenes, merging schedules with same sked_id
    const getAllSchedules = () => {
        const schedulesMap = {};
        if (activeBonds) {
            activeBonds.forEach(bond => {
                const bondData = bondsData[bond.bond_id];
                const bondTimezone = bondData?.sys?.time?.tz || 'UTC';

                // Helper function to process a schedule
                const processSchedule = (schedule, sourceType, sourceId, sourceName) => {
                    if (!schedule || typeof schedule !== 'object' || !schedule.sked_id) return;

                    const scheduleId = schedule.sked_id;
                    const calculatedTime = calculateScheduleTime(schedule.mark, schedule.seconds, bondTimezone);

                    const scheduleWithSource = {
                        ...schedule,
                        bond_id: bond.bond_id,
                        source_type: sourceType,
                        source_id: sourceId,
                        source_name: sourceName,
                        calculated_time: calculatedTime
                    };

                    if (schedulesMap[scheduleId]) {
                        // Merge schedule data
                        const existing = schedulesMap[scheduleId];

                        // Merge bond_ids
                        if (!existing.bond_ids.includes(bond.bond_id)) {
                            existing.bond_ids.push(bond.bond_id);
                        }

                        // Merge sources (track which devices/groups/scenes have this schedule)
                        existing.sources.push({
                            bond_id: bond.bond_id,
                            source_type: sourceType,
                            source_id: sourceId,
                            source_name: sourceName
                        });

                        // Store raw schedule objects for JSON viewer
                        existing.raw_schedules.push(scheduleWithSource);
                    } else {
                        // First time seeing this schedule
                        schedulesMap[scheduleId] = {
                            ...scheduleWithSource,
                            bond_ids: [bond.bond_id],
                            sources: [{
                                bond_id: bond.bond_id,
                                source_type: sourceType,
                                source_id: sourceId,
                                source_name: sourceName
                            }],
                            raw_schedules: [scheduleWithSource] // Store original schedule objects
                        };
                    }
                };

                // Get schedules from devices
                if (bondData?.devices) {
                    const devicesList = Array.isArray(bondData.devices)
                        ? bondData.devices
                        : Object.values(bondData.devices);

                    devicesList.forEach(device => {
                        if (device.schedules) {
                            const schedulesList = Array.isArray(device.schedules)
                                ? device.schedules
                                : Object.values(device.schedules);

                            schedulesList.forEach(schedule => {
                                processSchedule(schedule, 'device', device.id, device.name);
                            });
                        }
                    });
                }

                // Get schedules from groups
                if (bondData?.groups) {
                    const groupsList = Array.isArray(bondData.groups)
                        ? bondData.groups
                        : Object.values(bondData.groups);

                    groupsList.forEach(group => {
                        if (group.schedules) {
                            const schedulesList = Array.isArray(group.schedules)
                                ? group.schedules
                                : Object.values(group.schedules);

                            schedulesList.forEach(schedule => {
                                processSchedule(schedule, 'group', group.id, group.name);
                            });
                        }
                    });
                }

                // Get schedules from scenes
                if (bondData?.scenes) {
                    const scenesList = Array.isArray(bondData.scenes)
                        ? bondData.scenes
                        : Object.values(bondData.scenes);

                    scenesList.forEach(scene => {
                        if (scene.schedules) {
                            const schedulesList = Array.isArray(scene.schedules)
                                ? scene.schedules
                                : Object.values(scene.schedules);

                            schedulesList.forEach(schedule => {
                                processSchedule(schedule, 'scene', scene.id, scene.name);
                            });
                        }
                    });
                }
            });
        }
        return Object.values(schedulesMap);
    };

    const getAllSidekicks = () => {
        const sidekicksMap = {};
        if (activeBonds) {
            activeBonds.forEach(bond => {
                // Skip Mate devices (bonds starting with "SM")
                if (bond.bond_id.startsWith('SM')) {
                    return;
                }

                const bondData = bondsData[bond.bond_id];
                if (bondData?.sidekicks) {
                    // Handle both array and object formats
                    const sidekicksList = Array.isArray(bondData.sidekicks)
                        ? bondData.sidekicks
                        : Object.values(bondData.sidekicks);

                    sidekicksList.forEach(sidekick => {
                        const sidekickId = sidekick.sidekick_id || sidekick.id;
                        const sidekickData = sidekick.data || sidekick;

                        // Skip Breeze Pro devices (model: BWS-1000)
                        if (sidekickData.model === "BWS-1000") {
                            return;
                        }

                        // Only include sidekicks that start with "SH"
                        if (!sidekickId || !sidekickId.startsWith('SH')) {
                            return;
                        }

                        if (sidekicksMap[sidekickId]) {
                            // Merge sidekick data
                            const existing = sidekicksMap[sidekickId];

                            // Merge bond_ids
                            if (!existing.bond_ids.includes(bond.bond_id)) {
                                existing.bond_ids.push(bond.bond_id);
                            }

                            // Store raw sidekick objects for JSON viewer
                            existing.raw_sidekicks.push(sidekick);
                        } else {
                            // First time seeing this sidekick
                            sidekicksMap[sidekickId] = {
                                ...sidekick,
                                sidekick_id: sidekickId,
                                data: sidekickData,
                                bond_ids: [bond.bond_id],
                                raw_sidekicks: [sidekick] // Store original sidekick objects
                            };
                        }
                    });
                }
            });
        }
        return Object.values(sidekicksMap);
    };

    const getAllBreezePros = () => {
        const breezeProMap = {};
        if (activeBonds) {
            activeBonds.forEach(bond => {
                // Skip Mate devices (bonds starting with "SM")
                if (bond.bond_id.startsWith('SM')) {
                    return;
                }

                const bondData = bondsData[bond.bond_id];
                if (bondData?.sidekicks) {
                    // Handle both array and object formats
                    const sidekicksList = Array.isArray(bondData.sidekicks)
                        ? bondData.sidekicks
                        : Object.values(bondData.sidekicks);

                    sidekicksList.forEach(sidekick => {
                        const sidekickId = sidekick.sidekick_id || sidekick.id;
                        const sidekickData = sidekick.data || sidekick;

                        // Only include Breeze Pro devices (model: BWS-1000)
                        if (sidekickData.model !== "BWS-1000") {
                            return;
                        }

                        if (breezeProMap[sidekickId]) {
                            // Merge breeze pro data
                            const existing = breezeProMap[sidekickId];

                            // Merge bond_ids
                            if (!existing.bond_ids.includes(bond.bond_id)) {
                                existing.bond_ids.push(bond.bond_id);
                            }

                            // Store raw sidekick objects for JSON viewer
                            existing.raw_sidekicks.push(sidekick);
                        } else {
                            // First time seeing this breeze pro
                            breezeProMap[sidekickId] = {
                                ...sidekick,
                                sidekick_id: sidekickId,
                                data: sidekickData,
                                bond_ids: [bond.bond_id],
                                raw_sidekicks: [sidekick] // Store original sidekick objects
                            };
                        }
                    });
                }
            });
        }
        return Object.values(breezeProMap);
    };

    return (<>
        <Header
            onSearch={() => {
                const newSearchingState = !isSearching;
                setIsSearching(newSearchingState);

                if (newSearchingState) {
                    // Going to search mode - set search URL
                    // Keep selectedUser and data so user can return without losing state
                    window.location.hash = '#/';
                } else {
                    // Going back to viewing - restore URL if we have a selected user
                    if (selectedUser) {
                        window.location.hash = `#/u/${encodeURIComponent(selectedUser)}`;
                    }
                }
            }}
            bondTimezone={getBondTimezone()}
            bondTimezones={getAllBondTimezones()}
        />
        <Container fluid>
            {isSearching && <Row>
                <div className={"d-flex justify-content-center min-vh-50"}>
                    <Search onResultClick={(e) => {
                        if (e.owner_id) {
                            onSelectedUser(e.owner_id, e.id)
                        } else {
                            onSelectedUser(e.id)
                        }
                    }}/>
                </div>
            </Row>}

            <Row className={"mainRow"} style={{display: isSearching ? 'none' : 'flex'}}>
                <Col sm={12} lg={currentLayout.cards} className={"pb-3"}>
                    {userData && (
                        <>
                            <UserInfo
                                userData={userData}
                                isLoading={userDataLoading}
                                error={userDataError}
                                bondsData={bondsData}
                                bonds={activeBonds}
                                selectedHomeId={selectedHomeId}
                                onToggleLayout={toggleLayout}
                                currentLayoutLabel={currentLayout.label}
                                onRefresh={handleRefresh}
                            />

                            <HomesList
                                homes={homesList}
                                selectedHomeId={selectedHomeId}
                                onSelectHome={onSelectHome}
                                isLoading={homesLoading}
                                error={homesError}
                            />

                            {selectedHomeId && (
                                <HomeCard
                                    home={homeDetail}
                                    isLoading={homeDetailLoading}
                                    error={homeDetailError}
                                />
                            )}

                            {/* Bonds in a single collapsible accordion with carousel */}
                            <BondsList
                                bonds={getRegularBonds()}
                                bondsData={bondsData}
                                bondsLoading={bondsLoading}
                                bondsError={bondsError}
                            />

                            {/* Smart by Bond section */}
                            <SmartByBondList
                                smartByBonds={getSmartByBonds()}
                                bondsData={bondsData}
                                bondsLoading={bondsLoading}
                                bondsError={bondsError}
                            />

                            {/* Sidekicks section */}
                            <SidekickCard sidekicks={getAllSidekicks()} bondsData={bondsData} />

                            {/* Breeze Pro section */}
                            <BreezeProCard breezePros={getAllBreezePros()} bondsData={bondsData} />

                            {/* Mates in a single collapsible accordion */}
                            <MatesList
                                mates={getMates()}
                                matesData={bondsData}
                                matesLoading={bondsLoading}
                                matesError={bondsError}
                            />

                            {/* Additional card views for devices, groups, scenes, and schedules */}
                            <DeviceCard devices={getAllDevices()} />
                            <GroupCard groups={getAllGroups()} devices={getAllDevices()} />
                            <SceneCard scenes={getAllScenes()} devices={getAllDevices()} groups={getAllGroups()} />
                            <SkedCard schedules={getAllSchedules()} />
                        </>
                    )}
                </Col>
                <Col sm={12} lg={currentLayout.events}>
                    <EventStream bondIds={activeBonds.map((bond) => bond.bond_id)} bondsData={bondsData} userId={userData?.id} homeMembers={homeDetail?.members} homes={homesList} selectedHomeId={selectedHomeId} onSelectHome={onSelectHome} onRefresh={handleRefresh} onHomeChange={reloadHomes} />
                </Col>
            </Row>
        </Container>
    </>);
}

export default Dashboard;
